import { Link, useLocation, useNavigate } from "react-router-dom";
import { Heart, ArrowRight, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    if (location.pathname !== "/") {
      navigate(`/#${sectionId}`);
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white relative overflow-hidden">
      {/* Decorative gradient */}
      <div className="absolute top-0 left-1/2 w-[800px] h-[400px] bg-emerald-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />

      {/* Final CTA Section */}
      <div className="border-b border-slate-800">
        <div className="container-custom py-16 md:py-20 text-center relative">
          <h3 className="text-2xl md:text-3xl font-bold mb-4 font-[Outfit]">
            Bereit für deine Vorführung?
          </h3>
          <p className="text-slate-400 mb-8 max-w-md mx-auto">
            Kostenlos, unverbindlich und bei dir zuhause.
          </p>
          <Button
            onClick={() => scrollToSection("contact")}
            data-testid="footer-cta-button"
            className="btn-primary px-8 py-6 text-lg group"
          >
            Jetzt Termin sichern
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>

      <div className="container-custom py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <img src="/logo.png" alt="Jennifer Laak – Thermomix Beratung Krefeld" className="w-12 h-12 rounded-full object-cover bg-white shadow-lg" />
              <div className="leading-none">
                <span className="font-bold text-lg font-[Outfit] block">JENNIFER LAAK</span>
                <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Thermomix® Repräsentantin</span>
              </div>
            </div>
            <p className="text-slate-400 leading-relaxed mb-6 max-w-md">
              Erlebe den Thermomix® live bei dir zuhause. Kostenlose und 
              unverbindliche Vorführungen in Krefeld und 25km Umkreis – persönlich und nahbar.
            </p>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 max-w-md">
              <p className="text-sm text-slate-400">
                <strong className="text-slate-300">Selbstständige Thermomix® Repräsentantin für Vorwerk</strong>
                <br />
                <span className="text-slate-500">
                  Diese Website ist kein offizielles Angebot der Vorwerk Gruppe.
                </span>
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-white mb-5 font-[Outfit]">
              Navigation
            </h4>
            <ul className="space-y-3">
              {[
                { label: "Startseite", section: "hero" },
                { label: "Über mich", section: "about" },
                { label: "TM7", section: "tm7" },
                { label: "Leistungen", section: "erlebniskochen" },
                { label: "Standorte", section: "standorte" },
                { label: "Karriere", section: "karriere" },
                { label: "Kontakt", section: "contact" },
              ].map((link) => (
                <li key={link.section}>
                  <button
                    onClick={() => scrollToSection(link.section)}
                    data-testid={`footer-nav-${link.section}`}
                    className="text-slate-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Standorte – alle 15 für SEO-Linking */}
          <div className="lg:col-span-2">
            <h4 className="font-bold text-white mb-5 font-[Outfit]">
              Standorte
            </h4>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
              {[
                { slug: "krefeld", name: "Krefeld" },
                { slug: "meerbusch", name: "Meerbusch" },
                { slug: "willich", name: "Willich" },
                { slug: "toenisvorst", name: "Tönisvorst" },
                { slug: "kempen", name: "Kempen" },
                { slug: "viersen", name: "Viersen" },
                { slug: "moers", name: "Moers" },
                { slug: "neuss", name: "Neuss" },
                { slug: "kaarst", name: "Kaarst" },
                { slug: "grefrath", name: "Grefrath" },
                { slug: "nettetal", name: "Nettetal" },
                { slug: "korschenbroich", name: "Korschenbroich" },
                { slug: "moenchengladbach", name: "Mönchengladbach" },
                { slug: "duisburg", name: "Duisburg" },
                { slug: "duesseldorf", name: "Düsseldorf" },
              ].map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/standort/${s.slug}`}
                    data-testid={`footer-standort-${s.slug}`}
                    className="text-slate-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    Thermomix {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-bold text-white mb-5 font-[Outfit]">
              Rechtliches
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/impressum"
                  data-testid="footer-impressum"
                  className="text-slate-400 hover:text-emerald-400 transition-colors text-sm"
                >
                  Impressum
                </Link>
              </li>
              <li>
                <Link
                  to="/datenschutz"
                  data-testid="footer-datenschutz"
                  className="text-slate-400 hover:text-emerald-400 transition-colors text-sm"
                >
                  Datenschutz
                </Link>
              </li>
              <li>
                <button
                  onClick={() => {
                    localStorage.removeItem("cookieConsent");
                    window.location.reload();
                  }}
                  data-testid="footer-cookie-settings"
                  className="text-slate-400 hover:text-emerald-400 transition-colors text-sm"
                >
                  Cookie-Einstellungen
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-sm text-slate-500">
              © {currentYear} Jennifer Laak – Thermomix® Repräsentantin Krefeld
            </p>
            <a href="https://www.instagram.com/jennifer_laak" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-pink-600 transition cursor-pointer text-white">
              <Instagram className="w-5 h-5" />
            </a>
            <p className="text-sm text-slate-500 flex items-center gap-1.5">
              Mit <Heart className="w-4 h-4 text-red-400" fill="currentColor" /> erstellt
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
